import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Settings, Cloud, CloudOff, Upload, Download, Trash2, Loader2, 
  CheckCircle, AlertCircle, RefreshCw, HardDrive, Calendar, Clock,
  FileSpreadsheet, Smartphone, UserX, AlertTriangle, Globe, DollarSign, Languages, Save
} from 'lucide-react';
import { toast } from 'sonner';

const PAESI = [
  { code: 'CH', name: 'Svizzera' },
  { code: 'IT', name: 'Italia' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Germania' },
  { code: 'AT', name: 'Austria' },
  { code: 'ES', name: 'Spagna' },
  { code: 'PT', name: 'Portogallo' },
  { code: 'NL', name: 'Paesi Bassi' },
  { code: 'BE', name: 'Belgio' },
  { code: 'GB', name: 'Regno Unito' },
];

const VALUTE = [
  { code: 'CHF', symbol: 'CHF', name: 'Franco Svizzero' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'Sterlina' },
  { code: 'USD', symbol: '$', name: 'Dollaro USA' },
];

const LINGUE = [
  { code: 'it', name: 'Italiano' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
];

export default function Impostazioni() {
  const { t, i18n } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [driveConnected, setDriveConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Auto-backup settings
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [autoBackupFrequency, setAutoBackupFrequency] = useState(7);
  const [lastAutoBackup, setLastAutoBackup] = useState(null);
  const [savingAutoBackup, setSavingAutoBackup] = useState(false);
  
  // Export
  const [exporting, setExporting] = useState(false);
  
  // Preferences
  const [selectedPaese, setSelectedPaese] = useState(user?.paese || 'CH');
  const [selectedValuta, setSelectedValuta] = useState(user?.valuta || 'CHF');
  const [selectedLingua, setSelectedLingua] = useState(user?.lingua || i18n.language || 'it');
  const [savingPreferences, setSavingPreferences] = useState(false);
  
  // Delete Account
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteEmailConfirm, setDeleteEmailConfirm] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    checkDriveStatus();
    loadAutoBackupSettings();
    
    // Initialize preferences from user
    if (user) {
      setSelectedPaese(user.paese || user.country || 'CH');
      setSelectedValuta(user.valuta || user.currency || 'CHF');
      setSelectedLingua(user.lingua || i18n.language || 'it');
    }
    
    // Check for OAuth callback
    if (searchParams.get('drive_connected') === 'true') {
      toast.success(t('settings.drive.connected'));
      setDriveConnected(true);
    }
    if (searchParams.get('drive_error') === 'true') {
      toast.error(t('settings.drive.connectionError'));
    }
  }, [searchParams, user]);

  useEffect(() => {
    if (driveConnected) {
      loadBackups();
      // Check if auto-backup is due
      checkAutoBackup();
    }
  }, [driveConnected]);

  const checkDriveStatus = async () => {
    try {
      const response = await api.get('/drive/status');
      setDriveConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking drive status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAutoBackupSettings = async () => {
    try {
      const response = await api.get('/drive/auto-backup');
      setAutoBackupEnabled(response.data.enabled || false);
      setAutoBackupFrequency(response.data.frequency_days || 7);
      setLastAutoBackup(response.data.last_auto_backup);
    } catch (error) {
      console.error('Error loading auto-backup settings:', error);
    }
  };

  const saveAutoBackupSettings = async () => {
    setSavingAutoBackup(true);
    try {
      await api.put('/drive/auto-backup', {
        enabled: autoBackupEnabled,
        frequency_days: autoBackupFrequency
      });
      toast.success(t('settings.autoBackup.saved'));
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSavingAutoBackup(false);
    }
  };

  const checkAutoBackup = async () => {
    try {
      const response = await api.post('/drive/check-auto-backup');
      if (response.data.backup_created) {
        toast.success(t('settings.autoBackup.autoCreated'));
        loadBackups();
      }
    } catch (error) {
      console.error('Auto-backup check failed:', error);
    }
  };

  const connectDrive = async () => {
    try {
      const response = await api.get('/drive/connect');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(t('settings.drive.connectionError'));
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm(t('settings.drive.disconnectConfirm'))) return;
    
    try {
      await api.delete('/drive/disconnect');
      setDriveConnected(false);
      setBackups([]);
      toast.success(t('settings.drive.disconnected'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await api.get('/drive/backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      await api.post('/drive/backup');
      toast.success(t('settings.drive.backupCreated'));
      loadBackups();
    } catch (error) {
      toast.error(t('settings.drive.backupError'));
    } finally {
      setCreatingBackup(false);
    }
  };

  const restoreBackup = async (fileId) => {
    if (!window.confirm(t('settings.drive.restoreConfirm'))) return;
    
    setRestoringId(fileId);
    try {
      await api.post(`/drive/restore/${fileId}`);
      toast.success(t('settings.drive.restoreSuccess'));
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error(t('settings.drive.restoreError'));
    } finally {
      setRestoringId(null);
    }
  };

  const deleteBackup = async (fileId) => {
    if (!window.confirm(t('settings.drive.deleteConfirm'))) return;
    
    setDeletingId(fileId);
    try {
      await api.delete(`/drive/backup/${fileId}`);
      toast.success(t('success.deleted'));
      loadBackups();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const response = await api.get('/export/excel', { responseType: 'blob' });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vaultride_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('settings.export.success'));
    } catch (error) {
      toast.error(t('settings.export.error'));
    } finally {
      setExporting(false);
    }
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    try {
      await api.put('/auth/me', {
        paese: selectedPaese,
        valuta: selectedValuta,
        lingua: selectedLingua
      });
      
      // Change i18n language
      i18n.changeLanguage(selectedLingua);
      
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Preferenze salvate con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio delle preferenze');
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword || !deleteEmailConfirm) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await api.delete('/auth/me', {
        data: {
          password: deletePassword,
          confirm_email: deleteEmailConfirm
        }
      });
      
      toast.success('Account eliminato con successo');
      setDeleteDialogOpen(false);
      
      // Logout and redirect to login
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'eliminazione';
      toast.error(errorMsg);
    } finally {
      setDeletingAccount(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const kb = parseInt(bytes) / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5F1F]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">{t('settings.title')}</h1>
            <p className="page-subtitle">{t('settings.subtitle')}</p>
          </div>
        </div>

        {/* Export Excel Section */}
        <div className="bg-[#121212] border border-[#262626] p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileSpreadsheet className="w-6 h-6 text-green-500" />
            <h2 className="text-xl font-semibold text-white">{t('settings.export.title')}</h2>
          </div>
          <p className="text-[#9CA3AF] mb-4">{t('settings.export.description')}</p>
          <Button 
            onClick={exportExcel}
            disabled={exporting}
            className="bg-green-600 hover:bg-green-700"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {t('settings.export.downloadExcel')}
          </Button>
        </div>

        {/* Google Drive Backup Section */}
        <div className="bg-[#121212] border border-[#262626] p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <HardDrive className="w-6 h-6 text-[#FF5F1F]" />
            <h2 className="text-xl font-semibold text-white">{t('settings.drive.title')}</h2>
          </div>

          {/* Connection Status */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {driveConnected ? (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t('settings.drive.connectedStatus')}</span>
                </div>
                <Button 
                  onClick={disconnectDrive}
                  variant="outline"
                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                >
                  <CloudOff className="w-4 h-4 mr-2" />
                  {t('settings.drive.disconnect')}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-[#262626] text-[#9CA3AF]">
                  <AlertCircle className="w-5 h-5" />
                  <span>{t('settings.drive.notConnected')}</span>
                </div>
                <Button 
                  onClick={connectDrive}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                >
                  <Cloud className="w-4 h-4 mr-2" />
                  {t('settings.drive.connect')}
                </Button>
              </>
            )}
          </div>

          {driveConnected && (
            <>
              {/* Auto-Backup Settings */}
              <div className="bg-black/20 border border-[#262626] p-4 mb-6">
                <h3 className="text-white font-medium flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-[#FF5F1F]" />
                  {t('settings.autoBackup.title')}
                </h3>
                
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  {/* Toggle */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setAutoBackupEnabled(!autoBackupEnabled)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        autoBackupEnabled ? 'bg-[#FF5F1F]' : 'bg-[#262626]'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        autoBackupEnabled ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                    <span className="text-white">{t('settings.autoBackup.enable')}</span>
                  </div>
                  
                  {/* Frequency */}
                  <div className="flex items-center gap-2">
                    <span className="text-[#9CA3AF]">{t('settings.autoBackup.every')}</span>
                    <Select 
                      value={autoBackupFrequency.toString()} 
                      onValueChange={(v) => setAutoBackupFrequency(parseInt(v))}
                    >
                      <SelectTrigger className="w-24 bg-[#121212] border-[#262626]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#262626]">
                        <SelectItem value="1" className="text-white">1 {t('settings.autoBackup.day')}</SelectItem>
                        <SelectItem value="3" className="text-white">3 {t('settings.autoBackup.days')}</SelectItem>
                        <SelectItem value="7" className="text-white">7 {t('settings.autoBackup.days')}</SelectItem>
                        <SelectItem value="14" className="text-white">14 {t('settings.autoBackup.days')}</SelectItem>
                        <SelectItem value="30" className="text-white">30 {t('settings.autoBackup.days')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    onClick={saveAutoBackupSettings}
                    disabled={savingAutoBackup}
                    size="sm"
                    className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                  >
                    {savingAutoBackup && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t('common.save')}
                  </Button>
                </div>
                
                {lastAutoBackup && (
                  <p className="text-[#9CA3AF] text-sm">
                    {t('settings.autoBackup.lastBackup')}: {formatDate(lastAutoBackup)}
                  </p>
                )}
              </div>

              {/* Manual Backup Actions */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button 
                  onClick={createBackup}
                  disabled={creatingBackup}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {creatingBackup ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.drive.createBackup')}
                </Button>
                <Button 
                  onClick={loadBackups}
                  disabled={loadingBackups}
                  variant="outline"
                  className="border-[#262626]"
                >
                  {loadingBackups ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.drive.refresh')}
                </Button>
              </div>

              {/* Backup List */}
              <div className="space-y-3">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#FF5F1F]" />
                  {t('settings.drive.availableBackups')}
                </h3>
                
                {loadingBackups ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#FF5F1F] mx-auto" />
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-8 bg-black/20 border border-[#262626]">
                    <Cloud className="w-10 h-10 text-[#262626] mx-auto mb-3" />
                    <p className="text-[#9CA3AF]">{t('settings.drive.noBackups')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {backups.map((backup) => (
                      <div 
                        key={backup.id}
                        className="flex items-center justify-between p-4 bg-black/20 border border-[#262626] hover:border-[#FF5F1F]/30 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="text-white font-mono text-sm flex items-center gap-2">
                            {backup.name}
                            {backup.name.includes('auto') && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">AUTO</span>
                            )}
                          </p>
                          <p className="text-[#9CA3AF] text-xs flex items-center gap-4">
                            <span>{formatDate(backup.created_at)}</span>
                            <span>{formatSize(backup.size)}</span>
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => restoreBackup(backup.id)}
                            disabled={restoringId === backup.id}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            title={t('settings.drive.restore')}
                          >
                            {restoringId === backup.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            onClick={() => deleteBackup(backup.id)}
                            disabled={deletingId === backup.id}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500/10"
                            title={t('common.delete')}
                          >
                            {deletingId === backup.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sync Info */}
        <div className="bg-[#121212] border border-[#262626] p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-white">{t('settings.sync.title')}</h2>
          </div>
          <p className="text-[#9CA3AF] mb-4">{t('settings.sync.description')}</p>
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 w-fit">
            <Smartphone className="w-4 h-4" />
            <span>{t('settings.sync.autoEnabled')}</span>
          </div>
        </div>

        {/* User Preferences */}
        <div className="bg-[#121212] border border-[#262626] p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#FF5F1F]" />
            Preferenze Account
          </h2>
          
          {/* Email (read-only) */}
          <div className="flex items-center justify-between py-3 border-b border-[#262626] mb-4">
            <span className="text-[#9CA3AF]">Email</span>
            <span className="text-white">{user?.email}</span>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Paese */}
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#FF5F1F]" />
                Paese
              </Label>
              <Select value={selectedPaese} onValueChange={setSelectedPaese}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] text-white">
                  <SelectValue placeholder="Seleziona paese" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626]">
                  {PAESI.map(paese => (
                    <SelectItem key={paese.code} value={paese.code} className="text-white hover:bg-[#262626]">
                      {paese.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Valuta */}
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#FF5F1F]" />
                Valuta
              </Label>
              <Select value={selectedValuta} onValueChange={setSelectedValuta}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] text-white">
                  <SelectValue placeholder="Seleziona valuta" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626]">
                  {VALUTE.map(valuta => (
                    <SelectItem key={valuta.code} value={valuta.code} className="text-white hover:bg-[#262626]">
                      {valuta.symbol} - {valuta.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Lingua */}
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Languages className="w-4 h-4 text-[#FF5F1F]" />
                Lingua
              </Label>
              <Select value={selectedLingua} onValueChange={setSelectedLingua}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626] text-white">
                  <SelectValue placeholder="Seleziona lingua" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626]">
                  {LINGUE.map(lingua => (
                    <SelectItem key={lingua.code} value={lingua.code} className="text-white hover:bg-[#262626]">
                      {lingua.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <Button 
              onClick={handleSavePreferences}
              disabled={savingPreferences}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
            >
              {savingPreferences ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva Preferenze
            </Button>
          </div>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="bg-[#121212] border border-red-500/30 p-6">
          <h2 className="text-xl font-semibold text-red-500 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Zona Pericolosa
          </h2>
          <p className="text-[#9CA3AF] mb-4">
            L'eliminazione dell'account è permanente. Tutti i tuoi dati, moto, spese, viaggi e documenti saranno eliminati definitivamente e non potranno essere recuperati.
          </p>
          <Button 
            onClick={() => setDeleteDialogOpen(true)}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500/10"
          >
            <UserX className="w-4 h-4 mr-2" />
            Elimina il mio Account
          </Button>
        </div>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#262626]">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Conferma Eliminazione Account
            </DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">
              Questa azione è irreversibile. Tutti i tuoi dati saranno eliminati permanentemente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white">Inserisci la tua email per confermare</Label>
              <Input
                type="email"
                placeholder={user?.email}
                value={deleteEmailConfirm}
                onChange={(e) => setDeleteEmailConfirm(e.target.value)}
                className="bg-[#0a0a0a] border-[#262626] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Inserisci la tua password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="bg-[#0a0a0a] border-[#262626] text-white"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletePassword('');
                setDeleteEmailConfirm('');
              }}
              className="border-[#262626]"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleDeleteAccount}
              disabled={deletingAccount || !deletePassword || deleteEmailConfirm.toLowerCase() !== user?.email?.toLowerCase()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingAccount ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Elimina Permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
